import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { securityApi, auth } from '@/services/api';

interface SecurityAlert {
  id: number;
  table_name: string;
  record_id: number;
  column_name: string;
  pattern: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  snippet: string | null;
  detected_at: string;
  resolved: number;
}

interface IntegrityResult {
  ok: boolean;
  baselineExists: boolean;
  baselineDate?: string;
  message?: string;
  changed: string[];
  missing: string[];
  new: string[];
}

interface AlertStats {
  total: number;
  unresolved: number;
  byCategory: Array<{ category: string; count: number }>;
}

type Status = 'idle' | 'loading' | 'error';

// ── Severity styling helper ─────────────────────────
function severityBadge(sev: string): { cls: string; icon: string; label: string } {
  const map: Record<string, { cls: string; icon: string; label: string }> = {
    critical: { cls: 'bg-rose-500/10 text-rose-500 border-rose-500/25', icon: 'ri-error-warning-line', label: 'Critical' },
    high: { cls: 'bg-orange-500/10 text-orange-600 border-orange-500/25', icon: 'ri-alert-line', label: 'High' },
    medium: { cls: 'bg-amber-500/10 text-amber-600 border-amber-500/25', icon: 'ri-alarm-line', label: 'Medium' },
    low: { cls: 'bg-secondary-100 text-secondary-700 border-secondary-200', icon: 'ri-information-line', label: 'Low' },
  };
  return map[sev] || map.low;
}

const CATEGORY_LABELS: Record<string, string> = {
  gambling_spam: 'Gambling spam',
  xss: 'XSS / injection',
  redirect: 'Redirect / cloaking',
  pharma_spam: 'Pharma spam',
  financial_spam: 'Financial spam',
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function SecurityDashboardPage() {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [stats, setStats] = useState<AlertStats | null>(null);
  const [integrity, setIntegrity] = useState<IntegrityResult | null>(null);

  const [alertsStatus, setAlertsStatus] = useState<Status>('loading');
  const [integrityStatus, setIntegrityStatus] = useState<Status>('loading');
  const [scanning, setScanning] = useState(false);
  const [resolvingId, setResolvingId] = useState<number | null>(null);
  const [creatingBaseline, setCreatingBaseline] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userRole = (auth.getRole() || '').toLowerCase();
  const isAdmin = ['super_admin', 'admin', 'owner'].includes(userRole);

  const notify = useCallback((type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Load alerts + stats ─────────────────────────
  const fetchAlerts = useCallback(async () => {
    setAlertsStatus('loading');
    try {
      const [alertRes, statsRes] = await Promise.all([
        securityApi.alerts(),
        securityApi.stats(),
      ]);
      setAlerts((alertRes.data ?? []) as SecurityAlert[]);
      setStats((statsRes.data ?? null) as AlertStats | null);
      setAlertsStatus('idle');
    } catch (err) {
      setAlertsStatus('error');
      setToast({ type: 'error', text: err instanceof Error ? err.message : 'Failed to load security alerts.' });
    }
  }, [setToast]);

  // ── Load integrity status ───────────────────────
  const fetchIntegrity = useCallback(async () => {
    setIntegrityStatus('loading');
    try {
      const res = await securityApi.integrityCheck();
      setIntegrity(res.data as IntegrityResult);
      setIntegrityStatus('idle');
    } catch (err) {
      setIntegrityStatus('error');
      setToast({ type: 'error', text: err instanceof Error ? err.message : 'Failed to check integrity.' });
    }
  }, [setToast]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAlerts();
    fetchIntegrity();
  }, [isAdmin, fetchAlerts, fetchIntegrity]);

  // ── Actions ─────────────────────────────────────
  const runScan = async () => {
    setScanning(true);
    try {
      const res = await securityApi.runScan();
      const data = res.data as { scannedAt: string; alertsFound: number } | null;
      notify('success', `Scan complete — ${data?.alertsFound ?? 0} threat(s) detected.`);
      await fetchAlerts();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Scan failed.');
    } finally {
      setScanning(false);
    }
  };

  const resolveAlert = async (id: number) => {
    setResolvingId(id);
    try {
      await securityApi.resolveAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      setStats((prev) => (prev ? { ...prev, unresolved: Math.max(0, prev.unresolved - 1) } : prev));
      notify('success', 'Alert resolved.');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to resolve alert.');
    } finally {
      setResolvingId(null);
    }
  };

  const createBaseline = async () => {
    setCreatingBaseline(true);
    try {
      const res = await securityApi.createBaseline();
      const data = res.data as { fileCount: number } | null;
      notify('success', `Integrity baseline created (${data?.fileCount ?? 0} files).`);
      await fetchIntegrity();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to create baseline.');
    } finally {
      setCreatingBaseline(false);
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-rose-500/15 flex items-center justify-center mb-4">
            <i className="ri-shield-check-line text-rose-400 text-2xl" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950">Access restricted</h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-sm">
            The Security Dashboard is admin-only (super_admin / admin / owner). Contact an administrator if you need access.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Derived counts
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const highCount = alerts.filter((a) => a.severity === 'high').length;
  const filteredAlerts = severityFilter === 'all'
    ? alerts
    : alerts.filter((a) => a.severity === severityFilter);

  const integrityOk = !!integrity?.ok;
  const baselineExists = !!integrity?.baselineExists;

  const statCards = [
    { label: 'Unresolved Alerts', value: stats?.unresolved ?? alerts.length, icon: 'ri-alert-line', cls: 'bg-rose-500/15 text-rose-500', accent: true },
    { label: 'Critical', value: criticalCount, icon: 'ri-error-warning-line', cls: 'bg-rose-500/15 text-rose-500' },
    { label: 'High', value: highCount, icon: 'ri-alert-line', cls: 'bg-orange-500/15 text-orange-600' },
    { label: 'Total Tracked', value: stats?.total ?? 0, icon: 'ri-shield-check-line', cls: 'bg-primary-500/15 text-primary-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {toast && (
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700'
              : 'bg-rose-500/10 border-rose-500/25 text-rose-600'
          }`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} text-base`} />
            {toast.text}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Security Dashboard</h1>
            <p className="text-xs text-foreground-500">
              Monitor injected spam, XSS payloads, and file tampering across your deployed code and database
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchAlerts}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-background-200/60 bg-white text-foreground-500 hover:text-foreground-950 hover:border-accent-200 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" /> Refresh
            </button>
            <button
              onClick={runScan}
              disabled={scanning}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={`${scanning ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'} text-base`} />
              {scanning ? 'Scanning…' : 'Run Security Scan'}
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((card) => (
            <div key={card.label} className="rounded-xl border border-background-200/60 bg-white p-4">
              <div className="flex items-center justify-between">
                <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.cls}`}>
                  <i className={`${card.icon} text-lg`} />
                </span>
                <span className="text-2xl font-bold font-heading text-foreground-950">{card.value}</span>
              </div>
              <p className="text-xs text-foreground-500 mt-2">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Category breakdown */}
        {stats && stats.byCategory.length > 0 && (
          <div className="rounded-xl border border-background-200/60 bg-white p-4">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Unresolved by category</p>
            <div className="flex flex-wrap gap-2">
              {stats.byCategory.map((c) => (
                <span key={c.category} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                  {CATEGORY_LABELS[c.category] || c.category}
                  <span className="px-1.5 rounded-full bg-white text-foreground-700 text-[10px] font-bold">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Integrity panel */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-background-200/60">
            <div className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${integrityOk ? 'bg-emerald-500/15 text-emerald-500' : 'bg-rose-500/15 text-rose-500'}`}>
                <i className={`${integrityOk ? 'ri-shield-check-line' : 'ri-shield-flash-line'} text-base`} />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">File Integrity Monitor</p>
                <p className="text-[11px] text-foreground-500">
                  {integrityStatus === 'loading' ? 'Checking baseline…' : baselineExists
                    ? `Baseline from ${formatDate(integrity!.baselineDate || '')}`
                    : 'No baseline recorded yet'}
                </p>
              </div>
            </div>
            <button
              onClick={createBaseline}
              disabled={creatingBaseline || integrityStatus === 'loading'}
              className="flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg border border-background-200/60 bg-background-50 text-foreground-500 hover:text-foreground-950 text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={`${creatingBaseline ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} text-sm`} />
              {creatingBaseline ? 'Creating…' : 'Recreate Baseline'}
            </button>
          </div>
          <div className="p-4">
            {integrityStatus === 'loading' ? (
              <div className="space-y-2">
                <div className="h-4 bg-background-200/60 rounded animate-pulse" />
                <div className="h-4 w-2/3 bg-background-200/60 rounded animate-pulse" />
              </div>
            ) : integrityStatus === 'error' ? (
              <p className="text-sm text-rose-500">Failed to load integrity status. <button onClick={fetchIntegrity} className="underline font-medium cursor-pointer">Retry</button></p>
            ) : (
              <>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  integrityOk
                    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25'
                    : 'bg-rose-500/10 text-rose-500 border-rose-500/25'
                }`}>
                  <i className={`${integrityOk ? 'ri-check-double-line' : 'ri-close-circle-line'} text-sm`} />
                  {integrityOk ? 'All watched files match the baseline' : 'Integrity violation detected'}
                </div>
                {!integrityOk && integrity?.message && (
                  <p className="text-sm text-foreground-600 mt-2">{integrity.message}</p>
                )}
                {(integrity?.changed.length || integrity?.missing.length || integrity?.new.length) ? (
                  <div className="mt-3 space-y-2">
                    {integrity.changed.length > 0 && (
                      <FileList title="Changed" files={integrity.changed} tone="rose" icon="ri-edit-line" />
                    )}
                    {integrity.missing.length > 0 && (
                      <FileList title="Missing" files={integrity.missing} tone="orange" icon="ri-delete-bin-line" />
                    )}
                    {integrity.new.length > 0 && (
                      <FileList title="New" files={integrity.new} tone="amber" icon="ri-add-line" />
                    )}
                  </div>
                ) : (
                  baselineExists && (
                    <p className="text-sm text-foreground-400 mt-2">
                      {integrity?.changed.length ?? 0} changed · {integrity?.missing.length ?? 0} missing · {integrity?.new.length ?? 0} new
                    </p>
                  )
                )}
              </>
            )}
          </div>
        </div>

        {/* Alerts table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-background-200/60">
            <div>
              <h2 className="text-sm font-semibold text-foreground-950">Unresolved Security Alerts</h2>
              <p className="text-[11px] text-foreground-500">Detected in database content, listings, inquiries and reports</p>
            </div>
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map((sev) => (
                <button
                  key={sev}
                  onClick={() => setSeverityFilter(sev)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors cursor-pointer whitespace-nowrap ${
                    severityFilter === sev ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {sev === 'all' ? 'All' : sev}
                </button>
              ))}
            </div>
          </div>

          {alertsStatus === 'loading' ? (
            <div className="p-12 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 bg-background-200/60 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : alertsStatus === 'error' ? (
            <div className="p-12 text-center">
              <i className="ri-alert-line text-2xl text-rose-400 mb-2 block" />
              <p className="text-sm text-foreground-500">Failed to load security alerts. <button onClick={fetchAlerts} className="underline font-medium cursor-pointer">Retry</button></p>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="p-12 text-center">
              <i className="ri-shield-check-line text-3xl text-emerald-400 mb-2 block" />
              <p className="text-sm text-foreground-500">
                {severityFilter === 'all'
                  ? 'No unresolved security alerts. Everything looks clean.'
                  : `No unresolved ${severityFilter} severity alerts.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Severity</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Source</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Pattern / Snippet</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Detected</th>
                    <th className="text-right text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.map((a) => {
                    const badge = severityBadge(a.severity);
                    return (
                      <tr key={a.id} className="border-b border-background-200/50 hover:bg-background-200/40 transition-colors">
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${badge.cls}`}>
                            <i className={badge.icon} /> {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-xs font-medium text-foreground-950 whitespace-nowrap">{a.table_name}</p>
                          <p className="text-[10px] text-foreground-400">
                            {a.column_name} · record #{a.record_id}
                          </p>
                        </td>
                        <td className="px-5 py-3 max-w-[320px]">
                          <p className="text-xs font-semibold text-rose-500 whitespace-nowrap">“{a.pattern}”</p>
                          {a.snippet && (
                            <p className="text-[10px] text-foreground-400 mt-0.5 truncate" title={a.snippet}>
                              <i className="ri-code-s-slash-line mr-1" />
                              {a.snippet}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(a.detected_at)}</td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => resolveAlert(a.id)}
                            disabled={resolvingId === a.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/25 text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-emerald-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            title="Mark resolved"
                          >
                            <i className={`${resolvingId === a.id ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} text-sm`} />
                            Resolve
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function FileList({ title, files, tone, icon }: { title: string; files: string[]; tone: 'rose' | 'orange' | 'amber'; icon: string }) {
  const toneCls: Record<string, string> = {
    rose: 'bg-rose-500/10 text-rose-600 border-rose-500/25',
    orange: 'bg-orange-500/10 text-orange-600 border-orange-500/25',
    amber: 'bg-amber-500/10 text-amber-600 border-amber-500/25',
  };
  return (
    <div>
      <p className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${toneCls[tone]}`}>
        <i className={icon} /> {title} · {files.length}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {files.map((f) => (
          <li key={f} className="text-xs text-foreground-600 font-mono flex items-center gap-1.5">
            <i className="ri-file-code-line text-foreground-400 text-xs" /> {f}
          </li>
        ))}
      </ul>
    </div>
  );
}